import React, { useCallback, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'

import { Box } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { BraintreePayPalButtons } from '@paypal/react-paypal-js'
import * as Sentry from '@sentry/react'

import useClosure from 'common/hooks/useClosure'
import ApplePay from 'artkive/components/ApplePay'
import { isApplePayAvailable } from 'artkive/components/ApplePay/utils'
import { CheckoutContext } from 'artkive/contexts/CheckoutContext'
import { usePayLaterFunding } from 'artkive/hooks/expressCheckout'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import braintree from 'artkive/utils/braintreeClient'
import routes from 'artkive/utils/routes'

import { Buttons, HelperBox, HelperLink, HelperText, TermsBox, TermsText } from './PaymentOptions.style'

const useStyles = makeStyles(({ breakpoints }) => ({
  applePayButtonArea: {
    maxWidth: '280px',
    width: '100%',
    [breakpoints.down('sm')]: {
      maxWidth: 'unset',
    },
  },
}))

const PaymentOptions = ({
  price,
  currency,
  onApplePayCheckout,
  onPayPalCheckout,
  onApplePayAddressChange,
  onPayPalAddressChange,
  setError,
  onApplePayClick,
  onPayPalClick,
  requestAddress,
  disableApplePay = false,
  isPayPalAvailable = false,
  braintreePayPalClientRef,
  setPayPalPaymentId,
}) => {
  const isApplePaySupported = useMemo(() => isApplePayAvailable(), [])
  const isMobile = useMobileBreakpoint()
  const classes = useStyles()
  const { braintreeClientToken } = useContext(CheckoutContext)

  const isPayLaterFundingAvailable = usePayLaterFunding()

  const payPalButtonStyles = useMemo(
    () => ({
      layout: isMobile ? 'vertical' : 'horizontal',
      color: 'gold',
      shape: 'rect',
      height: 48,
      tagline: false,
      disableMaxWidth: true,
    }),
    [isMobile],
  )

  // Create Braintree namespace for PayPal buttons
  // NOTE: there is a bug in library - https://github.com/paypal/paypal-js/issues/538
  const braintreeNamespace = useMemo(() => {
    if (typeof window !== 'undefined' && braintreeClientToken) {
      return {
        client: braintree.client,
        paypalCheckout: braintree.paypalCheckout,
      }
    }
    return null
  }, [braintreeClientToken])

  const createOrder = useClosure(
    async (_data, actions) => {
      try {
        // Store the Braintree PayPal client reference for later use in address change
        braintreePayPalClientRef.current = actions.braintree

        const response = await actions.braintree.createPayment({
          flow: 'checkout',
          amount: price.total,
          currency,
          intent: 'capture',
          enableShippingAddress: requestAddress,
          lineItems: [{ quantity: 1, unitAmount: price.total, name: 'Artkive Box Order', kind: 'debit' }],
          // NOTE: is required for checkout + vault, but first - Reference Transactions must be enabled
          // requestBillingAgreement: true,
          // billingAgreementDescription: 'Save PayPal for future Artkive purchases',
        })

        setPayPalPaymentId(response)

        return response
      } catch (error) {
        setError && setError(error.response?.data?.message)
        Sentry.captureException(error)
      }
    },
    [price.total, setError, currency, braintreePayPalClientRef, setPayPalPaymentId, requestAddress],
  )

  const handlePayPalApprove = useCallback(
    async (data, actions) => {
      const payload = await actions.braintree.tokenizePayment(data)
      onPayPalCheckout(payload)
    },
    [onPayPalCheckout],
  )

  const paypalContainerMaxWidth = useMemo(() => {
    if (isMobile) return 'unset'

    return isPayLaterFundingAvailable ? '560px' : '280px'
  }, [isMobile, isPayLaterFundingAvailable])

  // NOTE: comment the next line to debug Apple Pay on linux
  if (!isApplePaySupported && !isPayPalAvailable) return null

  return (
    <div>
      <Buttons>
        {isApplePaySupported && (
          <Box className={classes.applePayButtonArea}>
            <ApplePay
              price={price}
              currencyCode={currency}
              onSubmit={onApplePayCheckout}
              onAddressChange={onApplePayAddressChange}
              setError={setError}
              onClick={onApplePayClick}
              requestAddress={requestAddress}
              disabled={disableApplePay}
            >
              Buy with
            </ApplePay>
          </Box>
        )}
        {isPayPalAvailable && (
          <Box style={{ width: '100%', maxWidth: paypalContainerMaxWidth }}>
            <BraintreePayPalButtons
              onClick={onPayPalClick}
              onApprove={handlePayPalApprove}
              createOrder={createOrder}
              onShippingAddressChange={onPayPalAddressChange}
              onShippingOptionsChange={onPayPalAddressChange}
              style={payPalButtonStyles}
              forceReRender={[payPalButtonStyles]}
              braintreeNamespace={braintreeNamespace}
            />
          </Box>
        )}
      </Buttons>
      <TermsBox>
        <TermsText variant='body1'>
          By selecting an express checkout option, you agree to our {isMobile && <br />}
          <HelperLink href={routes.tou} target='_blank'>
            Terms of Use
          </HelperLink>
          {', '}
          <HelperLink href={routes.privacy} target='_blank'>
            Privacy Policy
          </HelperLink>
          {', and '}
          <HelperLink href={routes.pricing} target='_blank'>
            Book Pricing
          </HelperLink>
          .
        </TermsText>
      </TermsBox>
      <HelperBox>
        <HelperText variant='body1'>or pay by card</HelperText>
      </HelperBox>
    </div>
  )
}

PaymentOptions.propTypes = {
  price: PropTypes.object.isRequired,
  onApplePayCheckout: PropTypes.func.isRequired,
  onPayPalCheckout: PropTypes.func.isRequired,
  onApplePayAddressChange: PropTypes.func.isRequired,
  onPayPalAddressChange: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  requestAddress: PropTypes.bool.isRequired,
  onApplePayClick: PropTypes.func,
  onPayPalClick: PropTypes.func,
  disableApplePay: PropTypes.bool,
  isPayPalAvailable: PropTypes.bool,
  currency: PropTypes.string.isRequired,
  braintreePayPalClientRef: PropTypes.object.isRequired,
  setPayPalPaymentId: PropTypes.func.isRequired,
}

export default PaymentOptions
